import { useEffect, useRef } from 'react';
import { isCharacterKeyPress } from '../util';

const FKeys = [
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "F10",
  "F12",
  "F13",
];

const FKeyCodes = [
  112,
  113,
  114,
  115,
  116,
  117,
  118,
  119,
  120,
  121,
  122,
  123,
];

const UpKey = 38;
const DownKey = 40;
const DeleteKey = 46;

export type EscHandler = () => boolean;
export type EnterHandler = (evt: KeyboardEvent) => boolean;
export type DeleteHandler = () => boolean;
export type UpHandler = () => boolean;
export type DownHandler = () => boolean;
export type FunctionHandler = (f: string) => boolean;
export type AltCharHandler = (f: string) => boolean;
export type CtrlCharHandler = (f: string) => boolean;
export type SaveHandler = () => any;

interface FunctionKeysProps {
  onEsc?: EscHandler,
  onEnter?: EnterHandler,
  onFunction?: FunctionHandler,
  onAltChar?: AltCharHandler,
  onCtrlChar?: CtrlCharHandler,
  onDelete?: DeleteHandler,
  onUp?: UpHandler,
  onDown?: DownHandler
  onSave?: SaveHandler
};

/**
 * useFunctionKeys - hook for registering callback functions for various special keyboard keys
 *
 * @export
 * @param {FunctionKeysProps} {
 *   onEsc, - callback for Esc key
 *   onEnter,  - callback for Enter key
 *   onFunction, - callback for F key - receives function key being pressed as string parameter
 *   onAltChar, - callback for Alt + Char - receives char key being pressed as string parameter
 *   onUp, - callback for key up
 *   onDown - callback for key down
 * }
 */
export default function useFunctionKeys({
  onEsc,
  onEnter,
  onFunction,
  onAltChar,
  onCtrlChar,
  onDelete,
  onUp,
  onDown,
  onSave,
}: FunctionKeysProps) {
  const handlers = useRef({
    onEsc,
    onEnter,
    onFunction,
    onAltChar,
    onCtrlChar,
    onDelete,
    onUp,
    onDown,
    onSave,
  });

  useEffect(() => {
    handlers.current = {
      onEsc,
      onEnter,
      onFunction,
      onAltChar,
      onCtrlChar,
      onDelete,
      onUp,
      onDown,
      onSave,
    };
  }, [onEsc, onEnter, onFunction, onAltChar, onCtrlChar, onDelete, onUp, onDown, onSave]);

  useEffect(() => {
    function handle(evt: KeyboardEvent) {
      evt = evt || window.event;
      let isEscape = false;
      let isEnter = false;
      let isFunction = false;
      const isAltChar = evt.altKey && isCharacterKeyPress(evt);
      const isCtrlChar = evt.ctrlKey && isCharacterKeyPress(evt);
      let isDelete = false;
      let fkey = "";
      let isUp = false;
      let isDown = false;
      let isSave = false;
      const keyCode = evt.keyCode;
      const isCtrlPressed = evt.ctrlKey;

      if ("key" in evt) {
        isEscape = (evt.key === "Escape" || evt.key === "Esc");
        isEnter = evt.key === 'Enter';
        isFunction = FKeys.includes(evt.key);
        fkey = isFunction ? evt.key : "";
        isDelete = evt.key === 'Delete';
        isDown = evt.key === 'ArrowDown';
        isUp = evt.key === 'ArrowUp';
        isSave = isCtrlPressed && evt.key.toLowerCase() === 's';
      } else {
        isEscape = (keyCode === 27);
        isEnter = (keyCode === 13);
        isFunction = FKeyCodes.includes(keyCode);
        fkey = isFunction ? `F${keyCode - 111}` : "";
        isDelete = keyCode === DeleteKey;
        isUp = keyCode === UpKey;
        isDown = keyCode === DownKey;
        isSave = isCtrlPressed && keyCode === 83;
      }

      const currentHandlers = handlers.current;

      if (isEscape && currentHandlers.onEsc && !evt.cancelBubble) {
        evt.preventDefault();
        const escHandled = currentHandlers.onEsc();
        if (escHandled) {
          evt.stopPropagation();
        }
      }

      if (isEnter && currentHandlers.onEnter && !evt.cancelBubble) {
        currentHandlers.onEnter(evt);
        evt.preventDefault();
      }

      if (isFunction && currentHandlers.onFunction && !evt.cancelBubble) {
        let actualFKey = fkey;
        if (evt.altKey) {
          actualFKey = `ALT+${actualFKey}`;
        }
        if (evt.shiftKey) {
          actualFKey = `SHIFT+${actualFKey}`;
        }
        if (isCtrlPressed) {
          actualFKey = `CTRL+${actualFKey}`;
        }
        const functionHandled = currentHandlers.onFunction(actualFKey);
        if (functionHandled) {
          evt.preventDefault();
          evt.stopPropagation();
        }
      }

      if (isAltChar && currentHandlers.onAltChar) {
        let char = String.fromCharCode(evt.keyCode);
        if (char) {
          char = char.toLowerCase();
        }

        if (char) {
          const onAltCharHandled = currentHandlers.onAltChar(char);
          if (onAltCharHandled) {
            evt.preventDefault();
            evt.stopPropagation();
          }
        }
      }

      if (isSave && currentHandlers.onSave && !evt.cancelBubble) {
        evt.preventDefault();
        const saveHandled = currentHandlers.onSave();
        if (saveHandled) {
          evt.stopPropagation();
        }
      }

      if (isCtrlChar && currentHandlers.onCtrlChar) {
        let char = String.fromCharCode(evt.keyCode);
        if (char) {
          char = char.toLowerCase();
        }

        if (char) {
          const onCtrlCharHandled = currentHandlers.onCtrlChar(char);
          if (onCtrlCharHandled) {
            evt.preventDefault();
            evt.stopPropagation();
          }
        }
      }

      if (isDelete && currentHandlers.onDelete) {
        const handled = currentHandlers.onDelete();
        if (handled) {
          evt.preventDefault();
          evt.stopPropagation();
        }
      }

      if (isUp && currentHandlers.onUp) {
        const handled = currentHandlers.onUp();
        if (handled) {
          evt.preventDefault();
          evt.stopPropagation();
        }
      }
      if (isDown && currentHandlers.onDown) {
        const handled = currentHandlers.onDown();
        if (handled) {
          evt.preventDefault();
          evt.stopPropagation();
        }
      }
    }

    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, []);
}